import { ListConfigurationCacheService } from '../list-builder/services/ListConfigurationCacheService';
import { ListNavigationService } from '../services/ListNavigationService';
import { clearAllWorkdStorage } from '../storage/workdStorage';
import { useAnnouncementsStore } from './announcementsStore';
import { useAuthStore } from './authStore';
import { useDynamicViewStore } from './dynamicViewStore';
import { useItemFollowStore } from './itemFollowStore';
import { useListStore } from './listStore';
import { usePreferencesStore } from './preferencesStore';
import { useReportPreferencesStore } from './reportPreferencesStore';

// Re-export all stores
export { useAuthStore } from './authStore';
export type {
	AuthInstance,
	AuthStoreActions,
	AuthStoreState,
	AuthUser,
} from './authStore';

export { useListStore } from './listStore';
export type {
	CachedCount,
	CachedCounts,
	FailedListEntry,
	ListNavigationContext,
	ListPreferences,
	ListSessionNavigationState,
	ListSortField,
	ListStoreActions,
	ListStoreState,
} from './listStore';

export { usePreferencesStore } from './preferencesStore';
export type {
	PreferencesStoreActions,
	PreferencesStoreState,
	RingCentralPreferences,
} from './preferencesStore';

export {
	isInAppComposeAvailable,
	openInAppCompose,
	useComposeEmailBridge,
} from './composeEmailBridge';
export type { ComposeEmailOptions } from './composeEmailBridge';

export { useAnnouncementsStore } from './announcementsStore';
export type {
	AnnouncementsStoreActions,
	AnnouncementsStoreState,
	CachedAnnouncements,
} from './announcementsStore';

export { useDynamicViewStore } from './dynamicViewStore';
export type {
	DynamicViewStoreActions,
	DynamicViewStoreState,
} from './dynamicViewStore';

export {
	currentItemFollowOwnerKey,
	ITEM_FOLLOW_MAX_AGE_MS,
	useItemFollowStore,
} from './itemFollowStore';
export type {
	ItemFollowLoadStatus,
	ItemFollowStoreActions,
	ItemFollowStoreState,
} from './itemFollowStore';

export {
	currentReportPreferencesOwnerKey,
	REPORT_PREFERENCES_MAX_AGE_MS,
	useReportPreferencesStore,
	withFavorite,
	withRecentView,
} from './reportPreferencesStore';
export type {
	ReportPreferencesLoadStatus,
	ReportPreferencesStoreActions,
	ReportPreferencesStoreState,
} from './reportPreferencesStore';

/**
 * Clear all Zustand stores and workd: prefixed storage.
 * This is the primary cleanup function called on logout.
 *
 * Clears:
 * - Auth store (tokens, user, instance, OAuth state)
 * - List store (counts, preferences, navigation state, filter state)
 * - Preferences store (language, theme, RingCentral, config-web settings)
 * - Announcements store (dismissed IDs, cached announcements)
 * - Item follow store (the signed-in user's followed items)
 * - Report preferences store (the signed-in user's favorite and recent reports)
 * - In-memory caches (ListConfigurationCacheService)
 * - All workd: prefixed localStorage keys
 * - All workd: prefixed sessionStorage keys
 */
export function clearAllStores(): void {
	console.debug('[stores] clearAllStores - CLEARING ALL STORES');
	console.trace('[stores] clearAllStores - stack trace');

	// Clear all Zustand stores
	useAuthStore.getState().clearAuth();
	useListStore.getState().clearAll();
	usePreferencesStore.getState().clearAll();
	useAnnouncementsStore.getState().clearAll();
	useDynamicViewStore.getState().clearView();
	// PD-14306 — the followed items belong to the user who just signed out.
	useItemFollowStore.getState().reset();
	// PD-16919 — so are the favorite and recently used reports.
	useReportPreferencesStore.getState().reset();

	// Clear in-memory caches that are tenant-specific
	ListConfigurationCacheService.clear();
	ListNavigationService.clearAllNavigationWindows();

	// Clear all workd: prefixed storage (localStorage + sessionStorage)
	clearAllWorkdStorage();
}
